/**
 * Download Helpers Module
 * 
 * This module provides utilities for downloading document data in various formats (CSV, JSON)
 * and handling both single and combined downloads. It supports multiple document models
 * including shipping labels and safety datasheets.
 * 
 * Features:
 * - Single and combined file downloads
 * - Multiple format support (CSV, JSON)
 * - Model-specific CSV formatting
 * - ZIP file generation for multiple downloads
 * 
 * @author Galo Alonso Peralta Espinoza
 * @date 04-17-2025
 */

import JSZip from 'jszip'
import { saveAs } from 'file-saver'

//## ===== Section 1 - Model Agnostic Helpers ===== ##//

/**
 * Fetches JSON data for one or multiple documents
 * @param {string|string[]} ids - Single ID or array of IDs
 * @returns {Promise<Object|Array>} JSON data (single object or array of objects)
 * @throws {Error} If the API request fails
 */
export async function fetchJsonForFile(ids) {
    // Always use POST with docIds array
    const docIds = Array.isArray(ids) ? ids : [ids];
    
    const resp = await fetch('/api/get-json-file', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ docIds })
    });
    
    if (!resp.ok) throw new Error(resp.statusText);
    const result = await resp.json();
    
    // Handle any individual document errors
    const errors = result.documents.filter(doc => doc.error);
    if (errors.length > 0) {
        console.warn('Some documents failed to load:', errors);
    }
    
    const validDocuments = result.documents.filter(doc => !doc.error);
    
    // Return single object for single ID, array for multiple IDs
    return Array.isArray(ids) ? validDocuments.map(doc => doc.data) : validDocuments[0].data;
}

/**
 * Creates a downloadable Blob from JSON data
 * @param {Object} jsonData - The JSON data to convert
 * @returns {Blob} A Blob containing the JSON data
 */
export function buildJsonBlob(jsonData) {
    return new Blob([JSON.stringify(jsonData, null, 2)], {
        type: 'application/json'
    });
}

/**
 * Routes JSON data to the appropriate CSV builder based on model type and format
 * @param {Object} jsonData - The JSON data to convert
 * @param {string} model - The document model type
 * @param {string} format - The CSV format type ('full-csv' or 'simple-csv')
 * @returns {string} The generated CSV string
 * @throws {Error} If the model type is not supported
 */
export function buildCsvFromJson(jsonData, model, format = 'full-csv') {
    switch(model) {
        case 'shipping-labels':
            return buildFullShippingLabelsCsvFromJson(jsonData);
        case 'safety-datasheets':
            switch(format) {
                case 'full-csv':
                    return buildFullSafetyDatasheetsCsvFromJson(jsonData);
                case 'simple-csv':
                    return buildSimpleSafetyDatasheetsCsvFromJson(jsonData);
                default:
                    throw new Error(`Unknown format type: ${format}. Cannot generate CSV for unsupported format.`);
            }
        default:
            throw new Error(`Unknown model type: ${model}. Cannot generate CSV for unsupported document model.`);
    }
}

/**
 * Adds document ID column to an existing CSV
 * @param {string} csv - The original CSV string
 * @param {number} docId - The document ID to add
 * @returns {string} CSV with document ID column added
 */
function addDocumentIdToCsv(csv, docId) {
    const [header, ...rows] = csv.split('\n');
    const newHeader = 'document_id,' + header;
    const newRows = rows
        .filter(row => row.trim()) // Skip empty rows
        .map(row => `${docId},${row}`);
    return [newHeader, ...newRows].join('\n');
}

/**
 * Combines multiple JSON documents into a single combined JSON structure
 * @param {Array<Object>} jsonDataArray - Array of JSON documents to combine
 * @returns {Object} The combined JSON structure
 * @throws {Error} If no documents are provided
 */
export function combineJsonDocuments(jsonDataArray) {
    if (!jsonDataArray || jsonDataArray.length === 0) {
        throw new Error('No JSON documents provided for combination');
    }

    // If there is only one document, return the document as is
    if (jsonDataArray.length === 1) {
        return {
            combined_document: true,
            total_uploads: 1,
            uploads: [
                {
                    upload_number: 1,
                    data: jsonDataArray[0]
                }
            ]
        };
    }
    
    // Create array of uploads, each maintaining its original structure
    const uploads = jsonDataArray.map((jsonData, index) => ({
        upload_number: index + 1,
        data: jsonData
    }));
    
    // Create combined JSON structure with uploads array
    return {
        combined_document: true,
        total_uploads: jsonDataArray.length,
        total_pages: jsonDataArray.reduce((sum, doc) => sum + (doc.pages ? doc.pages.length : 0), 0),
        model: jsonDataArray[0].model, // All should have same model when combining
        uploads: uploads
    };
}

/**
 * Combines multiple documents into a single CSV based on model type and format
 * @param {Array<Object>} jsonDataArray - Array of JSON documents to combine
 * @param {string} model - The document model type
 * @param {string} format - The CSV format type ('full-csv' or 'simple-csv')
 * @returns {string} The combined CSV string
 * @throws {Error} If no documents are provided or model type is not supported
 */
export function combineCsvDocuments(jsonDataArray, model, format = 'full-csv') {
    if (!jsonDataArray || jsonDataArray.length === 0) {
        throw new Error('No documents provided for CSV combination');
    }
    
    if (jsonDataArray.length === 1) {
        return buildCsvFromJson(jsonDataArray[0], model, format);
    }
    
    // For shipping labels: combine all entity types and pages
    if (model === 'shipping-labels') {
        return combineFullShippingLabelscsv(jsonDataArray);
    }
    
    // For safety datasheets: combine all entity structures  
    if (model === 'safety-datasheets') {
        switch(format) {
            case 'full-csv':
                return combineFullSafetyDatasheetscsv(jsonDataArray);
            case 'simple-csv':
                return combineSimpleSafetyDatasheetscsv(jsonDataArray);
            default:
                throw new Error(`Unknown format type: ${format}. Cannot generate CSV for unsupported format.`);
        }
    }
    
    throw new Error(`Unknown model type for CSV combination: ${model}`);
}

//## ===== Section 2 - Model Specific Helpers ===== ##//

//> Shipping Labels

/**
 * Builds a CSV string from shipping labels JSON data (Full format)
 * @param {Object} jsonData - The JSON data containing shipping label information
 * @returns {string} The generated CSV string
 */
export function buildFullShippingLabelsCsvFromJson(jsonData) {
    // Predetermined column order (your current order + any missing labels)
    // NOTE: document_id is NOT included here as it's only added for combined downloads
    const predeterminedOrder = [
        'PageNumber',
        'CompanyAddress',
        'CompanyName',
        'CompanyPhoneNumber',
        'CustomerAddress',
        'CustomerCity',
        'CustomerZip',
        'CustomerState',
        'CustomerName',
        'CustomerPhoneNumber',
        'ShippingCompany',
        'ShippingMethod',
        'TrackingNumber',
        'InTransitBy',
        'OrderId',
        'ProductName',
        'SKU',
        'SalePlatform',
        'SaleQuantity',
        'TotalSaleQuantity',
        'PackageCount',
        'PackageWeight'
    ];

    // Get all entity types from the data
    const allTypes = new Set();
    jsonData.pages.forEach(p =>
        Object.keys(p.entities || {}).forEach(k => allTypes.add(k))
    );

    // Build final column order: predetermined + any additional types found
    const finalTypes = [...predeterminedOrder, ...Array.from(allTypes).filter(type => !predeterminedOrder.includes(type))];

    let csv = finalTypes.join(',') + '\n';
    jsonData.pages.forEach(p => {
        let row = '';
        finalTypes.forEach(type => {
            if (type === 'PageNumber') {
                row += `${p.page_number}`;
            } else {
                const arr = p.entities[type] || [];
                const cell = arr
                    .map(v => {
                        const escapedValue = String(v)
                            .replace(/\r?\n/g, ' ')
                            .replace(/\t/g, ' ')
                            .replace(/"/g, '""');
                        return escapedValue;
                    })
                    .join('|');
                row += `,"${cell}"`;
            }
        });
        csv += row + '\n';
    });
    return csv;
}

/**
 * Combines multiple shipping labels documents into a single CSV (Full format)
 * @param {Array<Object>} jsonDataArray - Array of JSON data for shipping labels
 * @returns {string} The combined CSV string
 */
function combineFullShippingLabelscsv(jsonDataArray) {
    const allRows = [];
    
    jsonDataArray.forEach((jsonData, docIndex) => {
        const docId = docIndex + 1;
        // Generate CSV for this document
        const docCsv = buildFullShippingLabelsCsvFromJson(jsonData);
        // Add document ID column
        const csvWithDocId = addDocumentIdToCsv(docCsv, docId);
        // Skip header for all but first document
        const [, ...rows] = csvWithDocId.split('\n');
        allRows.push(...rows);
    });
    
    // Get header from first document
    const firstDocCsv = buildFullShippingLabelsCsvFromJson(jsonDataArray[0]);
    const [header] = firstDocCsv.split('\n');
    const newHeader = 'document_id,' + header;
    
    return [newHeader, ...allRows].join('\n');
}

//> Safety Datasheets

/**
 * Builds a CSV string from safety datasheets JSON data (Full format)
 * @param {Object} jsonData - The JSON data containing safety datasheet information
 * @returns {string} The generated CSV string
 */
export function buildFullSafetyDatasheetsCsvFromJson(jsonData) {
  
    // Step 1: Scan entire JSON to find max number of child properties
    let maxChildCount = 0;
    const allChildProperties = new Set();
    
    jsonData.pages.forEach(page => {
        Object.values(page.entities || {}).forEach(entityArray => {
            entityArray.forEach(entity => {
                if (typeof entity === 'object' && entity !== null) {
                    // This is a parent entity with child properties
                    const childProps = Object.keys(entity);
                    childProps.forEach(prop => allChildProperties.add(prop));
                    maxChildCount = Math.max(maxChildCount, childProps.length);
                }
            });
        });
    });
    
    // Step 2: Build CSV header
    let header = 'page_number,entity_label,entity_value';
    for (let i = 1; i <= maxChildCount; i++) {
        header += `,child${i}_label,child${i}_value`;
    }
    header += '\n';
    
    let csvRows = [header];
    
    // Step 3 & 4: Process each page and entity
    jsonData.pages.forEach(page => {
        const pageNumber = page.page_number;
        
        Object.entries(page.entities || {}).forEach(([entityLabel, entityArray]) => {
            entityArray.forEach(entity => {
                if (typeof entity === 'string') {
                    // Simple entity (scalar) - Step 3
                    const escapedEntityLabel = String(entityLabel)
                        .replace(/\r?\n/g, ' ')
                        .replace(/\t/g, ' ')
                        .replace(/"/g, '""');
                    const escapedEntity = String(entity)
                        .replace(/\r?\n/g, ' ')
                        .replace(/\t/g, ' ')
                        .replace(/"/g, '""');
                    let row = `${pageNumber},"${escapedEntityLabel}","${escapedEntity}"`;
                    // Add blank child columns
                    for (let i = 1; i <= maxChildCount; i++) {
                        row += ',,';
                    }
                    row += '\n';
                    csvRows.push(row);
                    
                } else if (typeof entity === 'object' && entity !== null) {
                    // Parent entity with child objects - Step 4
                    let row = `${pageNumber},"${String(entityLabel).replace(/\"/g, '"')}",""`;
                    
                    // Get child properties and sort them for consistent ordering
                    const childEntries = Object.entries(entity);
                    
                    // Fill child columns
                    for (let i = 1; i <= maxChildCount; i++) {
                        if (i - 1 < childEntries.length) {
                            const [childLabel, childValue] = childEntries[i - 1];
                            const escapedLabel = String(childLabel)
                                .replace(/\r?\n/g, ' ')
                                .replace(/\t/g, ' ')
                                .replace(/"/g, '""');
                            const escapedValue = String(childValue || '')
                                .replace(/\r?\n/g, ' ')
                                .replace(/\t/g, ' ')
                                .replace(/"/g, '""');
                            row += `,"${escapedLabel}","${escapedValue}"`;
                        } else {
                            // Pad unused child columns with blanks
                            row += ',,';
                        }
                    }
                    row += '\n';
                    csvRows.push(row);
                }
            });
        });
    });
    
    return csvRows.join('');
}

/**
 * Combines multiple safety datasheets documents into a single CSV (Full format)
 * @param {Array<Object>} jsonDataArray - Array of JSON data for safety datasheets
 * @returns {string} The combined CSV string
 */
function combineFullSafetyDatasheetscsv(jsonDataArray) {
    const allRows = [];
    
    jsonDataArray.forEach((jsonData, docIndex) => {
        const docId = docIndex + 1;
        // Generate CSV for this document
        const docCsv = buildFullSafetyDatasheetsCsvFromJson(jsonData);
        // Add document ID column
        const csvWithDocId = addDocumentIdToCsv(docCsv, docId);
        // Skip header for all but first document
        const [, ...rows] = csvWithDocId.split('\n');
        allRows.push(...rows);
    });
    
    // Get header from first document
    const firstDocCsv = buildFullSafetyDatasheetsCsvFromJson(jsonDataArray[0]);
    const [header] = firstDocCsv.split('\n');
    const newHeader = 'document_id,' + header;
    
    return [newHeader, ...allRows].join('\n');
}

/**
 * Builds a simplified CSV string from safety datasheets JSON data
 * Each document is represented as a single row with Section 1 and 2 data
 * @param {Object} jsonData - The JSON data containing safety datasheet information
 * @returns {string} The generated CSV string
 */
export function buildSimpleSafetyDatasheetsCsvFromJson(jsonData) {
    // Helper to combine multiple values with semicolon
    const combineValues = (values) => {
        if (!values || values.length === 0) return '';
        return values.map(v => String(v)
            .replace(/\r?\n/g, ' ')
            .replace(/\t/g, ' ')
            .replace(/"/g, '""')
        ).join(';');
    };

    // Helper to process GHS classifications
    const processGHSClassifications = (classifications) => {
        if (!classifications || classifications.length === 0) return '';
        return classifications.map(c => {
            if (typeof c === 'object' && c !== null) {
                // Use correct property names as in the data: 'Hazard Type' and 'Category'
                return `${c['Hazard Type'] || ''} - ${c['Category'] || ''}`;
            }
            return String(c);
        }).join(';');
    };

    // Get all Section 1 and 2 entities
    const s1Entities = {};
    const s2Entities = {};
    
    jsonData.pages.forEach(page => {
        Object.entries(page.entities || {}).forEach(([key, value]) => {
            if (key.startsWith('S1_')) {
                const cleanKey = key.replace('S1_', '');
                s1Entities[cleanKey] = value;
            } else if (key.startsWith('S2_')) {
                const cleanKey = key.replace('S2_', '');
                s2Entities[cleanKey] = value;
            }
        });
    });

    // Build header
    const headers = [
        ...Object.keys(s1Entities),
        ...Object.keys(s2Entities)
    ];

    // Build row
    const row = headers.map(header => {
        let value = '';
        if (s1Entities[header]) {
            value = combineValues(s1Entities[header]);
        } else if (s2Entities[header]) {
            if (header === 'GHS_Classification') {
                value = processGHSClassifications(s2Entities[header]);
            } else {
                value = combineValues(s2Entities[header]);
            }
        }
        return `"${value}"`;
    });

    return [headers.join(','), row.join(',')].join('\n');
}

/**
 * Combines multiple safety datasheets documents into a single CSV (Simple format)
 * @param {Array<Object>} jsonDataArray - Array of JSON data for safety datasheets
 * @returns {string} The combined CSV string
 */
function combineSimpleSafetyDatasheetscsv(jsonDataArray) {
    const allRows = [];
    
    jsonDataArray.forEach((jsonData, docIndex) => {
        const docId = docIndex + 1;
        // Generate CSV for this document
        const docCsv = buildSimpleSafetyDatasheetsCsvFromJson(jsonData);
        // Add document ID column
        const csvWithDocId = addDocumentIdToCsv(docCsv, docId);
        // Skip header for all but first document
        const [, ...rows] = csvWithDocId.split('\n');
        allRows.push(...rows);
    });
    
    // Get header from first document
    const firstDocCsv = buildSimpleSafetyDatasheetsCsvFromJson(jsonDataArray[0]);
    const [header] = firstDocCsv.split('\n');
    const newHeader = 'document_id,' + header;
    
    return [newHeader, ...allRows].join('\n');
}

//## ===== Section 3 - Main Download Functions ===== ##//

/**
 * Main download function for handling single and multiple file downloads
 * @param {string[]} ids - List of document IDs to fetch & download
 * @param {'csv'|'json'} format - Which format to build
 * @param {function(string):string} getName - Function to get download base name from ID
 * @param {function(string):string} getModel - Function to get document model type
 * @param {boolean} combinedDownload - Whether to combine files of same model type
 * @param {string} csvFormat - The CSV format type ('full-csv' or 'simple-csv')
 */
export async function downloadFiles(ids, format, getName, getModel = null, combinedDownload = false, csvFormat = 'full-csv') {
    if (!ids || !ids.length) return
  
    //? 1) Single-file
    if (ids.length === 1) {
      const id   = ids[0]
      const name = getName(id)
      const json = await fetchJsonForFile(id)

      //? 1.a) JSON
      if (format === 'json') {
        const blob = buildJsonBlob(json)
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        a.download = `${name}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

      //? 1.b) CSV
      } else {
        const model = getModel ? getModel(id) : json.model
        const csv = buildCsvFromJson(json, model, csvFormat)
        const uri = encodeURI(`data:text/csv;charset=utf-8,${csv}`)
        const a   = document.createElement('a')
        a.href     = uri
        a.download = `${name}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
      return
    }
  
    //? 2) Multiple files
    if (combinedDownload) {
      // Combined download logic
      await handleCombinedDownload(ids, format, getName, getModel, csvFormat)
    } else {
      // Original separate files → zip logic  
      await handleSeparateDownload(ids, format, getName, getModel, csvFormat)
    }
}

/**
 * Handles combined download of multiple files
 * @param {string[]} ids - List of document IDs to fetch & download
 * @param {'csv'|'json'} format - Which format to build
 * @param {function(string):string} getName - Function to get download base name from ID
 * @param {function(string):string} getModel - Function to get document model type
 * @param {string} csvFormat - The CSV format type ('full-csv' or 'simple-csv')
 */
async function handleCombinedDownload(ids, format, getName, getModel, csvFormat = 'full-csv') {
  // Fetch all JSON documents in one bulk call
  const jsonDataArray = await fetchJsonForFile(ids)
  
  // Create documents with metadata
  const documentsWithMetadata = ids.map((id, index) => {
    const json = jsonDataArray[index]
    const model = getModel ? getModel(id) : json.model
    return { id, json, model, name: getName(id) }
  })
  
  // Group by model type
  const groupedByModel = {}
  documentsWithMetadata.forEach(({ id, json, model, name }) => {
    if (!groupedByModel[model]) {
      groupedByModel[model] = []
    }
    groupedByModel[model].push({ id, json, model, name })
  })
  
  // Check if multiple model types selected
  const modelTypes = Object.keys(groupedByModel)
  if (modelTypes.length > 1) {
    alert('Combined downloads with different document types are not supported due to varying output formats. Please select files of the same type or uncheck "Combined Download".')
    return
  }
  
  // Process the single model type
  const model = modelTypes[0]
  const documents = groupedByModel[model]
  const combinedJsonDataArray = documents.map(doc => doc.json)
  
  if (format === 'json') {
    // Combined JSON
    const combinedJson = combineJsonDocuments(combinedJsonDataArray)
    const blob = buildJsonBlob(combinedJson)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `combined_${model.replace(/-/g, '_')}_${documents.length}files.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
  } else {
    // Combined CSV
    const combinedCsv = combineCsvDocuments(combinedJsonDataArray, model, csvFormat)
    const uri = encodeURI(`data:text/csv;charset=utf-8,${combinedCsv}`)
    const a = document.createElement('a')
    a.href = uri
    a.download = `combined_${model.replace(/-/g, '_')}_${documents.length}files.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
}

/**
 * Handles separate download of multiple files into a ZIP
 * @param {string[]} ids - List of document IDs to fetch & download
 * @param {'csv'|'json'} format - Which format to build
 * @param {function(string):string} getName - Function to get download base name from ID
 * @param {function(string):string} getModel - Function to get document model type
 * @param {string} csvFormat - The CSV format type ('full-csv' or 'simple-csv')
 */
async function handleSeparateDownload(ids, format, getName, getModel, csvFormat = 'full-csv') {
  const zip = new JSZip()
  
  // Fetch all JSON documents in one bulk call
  const jsonDataArray = await fetchJsonForFile(ids)
  
  // Process each document
  ids.forEach((id, index) => {
    const base = getName(id)
    const json = jsonDataArray[index]

    if (format === 'json') {
      zip.file(`${base}.json`, buildJsonBlob(json))
    } else {
      const model = getModel ? getModel(id) : json.model
      zip.file(`${base}.csv`, buildCsvFromJson(json, model, csvFormat))
    }
  })
  
  const blob = await zip.generateAsync({ type: 'blob' })
  saveAs(blob, `extractai_downloadedfiles_${format}.zip`)
}